import { Phone, MapPin, Mail, Clock, MessageCircle } from "lucide-react";
import { useSettings, telHref } from "@/context/SettingsContext";
import { useSeo } from "@/lib/useSeo";
import { EnquiryForm } from "@/components/site/EnquiryForm";
import { PublicLayout } from "@/components/site/PublicLayout";

export default function Contact() {
  const settings = useSettings();
  const c = settings?.contact || {};
  useSeo({
    title: "Contact | Adv. Guruvayoorappan K V, Advocate Thrissur",
    description: "Contact Adv. Guruvayoorappan K V, Advocate, Thrissur. Office at KMA Towers, Kanjani Road, West Fort, Thrissur, Kerala – 680004.",
  });

  const whatsappDigits = (c.whatsapp || "").replace(/[^0-9]/g, "");

  return (
    <PublicLayout>
      <section className="bg-navy py-14 lg:py-20" data-testid="contact-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <p className="text-brass font-mono text-xs tracking-[0.25em] uppercase mb-4">Get in Touch</p>
          <h1 className="font-serif text-4xl lg:text-5xl text-ivory leading-tight mb-2">ADV. GURUVAYOORAPPAN K V</h1>
          <p className="text-brass text-lg font-serif italic">Advocate · Thrissur, Kerala</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-14 grid lg:grid-cols-2 gap-12">
        {/* Info */}
        <div>
          <h2 className="font-serif text-2xl text-navy mb-6">Office Contact</h2>
          <div className="space-y-5">
            <div className="flex gap-4" data-testid="contact-address">
              <span className="w-11 h-11 rounded-sm bg-navy text-brass flex items-center justify-center shrink-0"><MapPin size={19} /></span>
              <div>
                <p className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-1">Office Address</p>
                <p className="text-navy leading-relaxed">{c.address}</p>
              </div>
            </div>
            <div className="flex gap-4">
              <span className="w-11 h-11 rounded-sm bg-navy text-brass flex items-center justify-center shrink-0"><Phone size={19} /></span>
              <div>
                <p className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-1">Phone</p>
                <a href={telHref(c.phone1)} className="text-navy hover:text-brass block transition-colors" data-testid="contact-phone1">{c.phone1}</a>
                <a href={telHref(c.phone2)} className="text-navy hover:text-brass block transition-colors" data-testid="contact-phone2">{c.phone2}</a>
              </div>
            </div>
            <div className="flex gap-4">
              <span className="w-11 h-11 rounded-sm bg-navy text-brass flex items-center justify-center shrink-0"><Mail size={19} /></span>
              <div>
                <p className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-1">Email</p>
                {c.email && c.email.includes("@") ? <a href={`mailto:${c.email}`} className="text-navy hover:text-brass transition-colors" data-testid="contact-email">{c.email}</a> : <p className="text-navy">{c.email}</p>}
              </div>
            </div>
            <div className="flex gap-4">
              <span className="w-11 h-11 rounded-sm bg-navy text-brass flex items-center justify-center shrink-0"><Clock size={19} /></span>
              <div>
                <p className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-1">Office Hours</p>
                <p className="text-navy">{c.office_hours}</p>
              </div>
            </div>
            {whatsappDigits && (
              <a href={`https://wa.me/${whatsappDigits}`} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#25D366] text-white font-semibold px-6 py-3 rounded-sm hover:opacity-90 transition-opacity" data-testid="contact-whatsapp">
                <MessageCircle size={18} /> WhatsApp
              </a>
            )}
          </div>

          {/* Map */}
          {c.map_url && (
            <div className="mt-8 rounded-lg overflow-hidden border border-slate-200" style={{ aspectRatio: "16/10" }} data-testid="contact-map">
              <iframe title="Office location" src={c.map_url} className="w-full h-full" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
            </div>
          )}
        </div>

        {/* Form */}
        <div>
          <div className="bg-white border border-slate-200 rounded-lg p-6 lg:p-8 shadow-sm">
            <h2 className="font-serif text-2xl text-navy mb-1">Send an Enquiry</h2>
            <p className="text-slate-500 text-sm mb-6">Describe your legal matter and we will review it.</p>
            <EnquiryForm />
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}
